import React, { useState, useEffect } from 'react';
import './Hero.css';
import { analytics } from '../../lib/analytics';
import { emailService } from '../../lib/emailService';

interface HeroProps {
  onRequestDemoClick: () => void;
  onSurveyTrigger?: () => void;
}

const Hero: React.FC<HeroProps> = ({ onRequestDemoClick, onSurveyTrigger }) => {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isDuplicate, setIsDuplicate] = useState(false);

  // Load email and submission status from sessionStorage on mount
  useEffect(() => {
    const updateFromStorage = () => {
      const storedEmail = sessionStorage.getItem('userEmail');
      const isSubmitted = sessionStorage.getItem('waitlistSubmitted') === 'true';
      
      if (storedEmail) {
        setEmail(storedEmail);
      }
      
      if (isSubmitted) {
        setSubmitStatus('submitted');
      }
    };

    // Initial load
    updateFromStorage();

    // Listen for custom storage events
    const handleStorageChange = () => {
      updateFromStorage();
    };

    window.addEventListener('waitlistUpdated', handleStorageChange);

    // Track page view and form view (only if consent given)
    analytics.trackPageView();
    analytics.trackInteraction('hero_form_view');

    return () => {
      window.removeEventListener('waitlistUpdated', handleStorageChange);
    };
  }, []);

  // Force video muted before React renders to ensure autoplay on mobile
  useEffect(() => {
    const vid = document.querySelector('.hero-video') as HTMLVideoElement;
    if (vid) vid.muted = true;
  }, []);

  // Save email to sessionStorage whenever it changes
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Track form start on first character
    if (newEmail.length === 1 && email.length === 0) {
      analytics.trackFormStart('hero_waitlist_form');
    }
    
    // Track each character entry to user_interactions with JSONB data { text }
    analytics.trackInputChange('hero_waitlist_email', newEmail);
    
    // If email changed and status was submitted, reset to idle to allow resubmit
    const storedEmail = sessionStorage.getItem('userEmail');
    if (submitStatus === 'submitted' && newEmail !== storedEmail) {
      setSubmitStatus('idle');
      setIsDuplicate(false);
      setErrorMessage('');
    }
    
    if (newEmail.trim()) {
      sessionStorage.setItem('userEmail', newEmail);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitStatus !== 'idle' && submitStatus !== 'error') return;
    
    // Set submitting state
    setSubmitStatus('submitting');
    setErrorMessage('');
    
    try {
      // Validate email
      if (!email || !email.includes('@')) {
        setErrorMessage('Please enter a valid email address');
        setSubmitStatus('error');
        return;
      }

      // Track form submission (only if consent given)
      analytics.trackFormSubmit('hero_waitlist_form', { email });
      
      const result = await analytics.createUser(email, 'hero');
      
      if (!result.success) {
        setErrorMessage('Failed to save your email. Please try again.');
        setSubmitStatus('error');
        return;
      }
      
      if (result.isDuplicate) {
        console.log('User already on waitlist');
        sessionStorage.setItem('userEmail', email);
        sessionStorage.setItem('waitlistSubmitted', 'true');
        
        window.dispatchEvent(new CustomEvent('waitlistUpdated'));
        
        setIsDuplicate(true);
        setSubmitStatus('submitted');
        return;
      }
      
      setIsDuplicate(false);
      
      console.log('User created successfully in Supabase');
      
      sessionStorage.setItem('userEmail', email);
      sessionStorage.setItem('waitlistSubmitted', 'true');
      
      window.dispatchEvent(new CustomEvent('waitlistUpdated'));
      
      setSubmitStatus('submitted');
      
      try {
        const emailSent = await emailService.sendWelcomeEmail(email);
        if (emailSent) {
          console.log('Welcome email sent successfully via SendGrid');
        } else {
          console.warn('Email sending failed, but user was saved');
        }
      } catch (emailError) {
        console.error('Email sending error:', emailError);
      }
      
      analytics.trackInteraction('waitlist_success', 'hero_form');
      
      if (onSurveyTrigger) {
        setTimeout(() => {
          onSurveyTrigger();
        }, 1000);
      }
    } catch (error) {
      console.error('Error submitting waitlist:', error);
      setErrorMessage('Something went wrong. Please try again.');
      setSubmitStatus('error');
    }
  };

  return (
    <section className="hero">
      <div className="hero-container">
        <div className="hero-content">
          <h1 className="hero-title">
          An architectural intelligence platform built for fast approvals.
            <span className="hero-title-accent"> </span>
          </h1>
          <p className="hero-subtitle">
          Connect projects, compliance and collaboration into one seamless AI-powered workflow – eliminating rework and lost fees.
          </p>
          <div className="hero-actions">
            <form className="hero-waitlist-form" onSubmit={handleSubmit}>
              <div className="hero-input-container">
                <input
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={handleEmailChange}
                  className="hero-email-input"
                  required
                  disabled={submitStatus === 'submitting'}
                />
                <button 
                  type="submit" 
                  className={`hero-waitlist-button ${submitStatus === 'submitting' ? 'hero-waitlist-button--loading' : ''}`}
                  disabled={submitStatus === 'submitting'}
                >
                  {submitStatus === 'idle' && 'Join Waitlist'}
                  {submitStatus === 'error' && 'Try Again'}
                  {submitStatus === 'submitting' && (
                    <>
                      <span className="loading-spinner"></span>
                      Joining...
                    </>
                  )}
                  {submitStatus === 'submitted' && (
                    <>
                      <span className="success-checkmark">✓</span>
                      Thank you!
                    </>
                  )}
                </button>
              </div>
              {submitStatus === 'submitted' ? (
                <p className="hero-success-message" style={{ color: '#10b981' }}>
                  {isDuplicate ? "You're already on our waitlist!" : "Welcome to the waitlist! We'll be in touch soon."}
                </p>
              ) : submitStatus === 'error' ? (
                <p className="hero-success-message" style={{ color: '#ef4444' }}>{errorMessage}</p>
              ) : (
                <p className="hero-privacy">We will never share your information with anyone.</p>
              )}
            </form>
            <div className="hero-social-proof">
              <div className="hero-avatars">
                <img src="/headshot6.jpg" alt="User 6" className="hero-avatar" />
                <img src="/headshot12.jpg" alt="User 12" className="hero-avatar" />
                <img src="/headshot8.jpg" alt="User 8" className="hero-avatar" />
                <img src="/headshot9.jpg" alt="User 9" className="hero-avatar" />
                <div className="hero-avatar hero-avatar-plus">+</div>
              </div>
            </div>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">87%</span>
              <span className="stat-label">Delayed Approvals</span>
            </div>
            <div className="stat">
              <span className="stat-number">21%</span>
              <span className="stat-label">Avoidable Rework</span>
            </div>
            <div className="stat">
              <span className="stat-number">£21B+</span>
              <span className="stat-label">Fees Burned</span>
            </div>
          </div>
          <p className="hero-stats-note">
            *<a 
              href="https://www.gov.uk/government/statistics/planning-applications-in-england-april-to-june-2025/planning-applications-in-england-april-to-june-2025-statistical-release" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hero-stats-link"
            >
              UK Government
            </a>
            {' '}& <a 
              href="https://getitright.uk.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hero-stats-link"
            >
              Industry Data
            </a>
            , 2024-25
          </p>
        </div>
        <div className="hero-visual">
          <div className="hero-image-placeholder">
            <div className="mockup-window">
              <div className="mockup-header">
                <div className="mockup-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div className="mockup-content">
                  <video
                    className="hero-video"
                    src="/landing-video.mp4"
                    autoPlay
                    muted
                    loop
                    playsInline
                    webkit-playsinline="true"
                    preload="auto"
                  />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 